import React from "react";
import { RefreshCw } from "lucide-react";

type StopsValue = "none" | "one-or-fewer" | "one" | "two" | "three" | null;

interface StopsFilterProps {
  stopsFilter: StopsValue;
  onStopsFilterChange: (filter: StopsValue) => void;
  displayedResultCount: number;
  hideInternalFilters?: boolean;
  hideConnectedFilter?: boolean;
}

const OPTIONS: {
  value: Exclude<StopsValue, null>;
  label: string;
  connectedOnly?: boolean;
}[] = [
  { value: "none", label: "Non-stop" },
  { value: "one", label: "1 Stop" },
  { value: "two", label: "2 Stops" },
  { value: "three", label: "3 Stops" },
  { value: "one-or-fewer", label: "Connected", connectedOnly: true },
];

const StopsFilter: React.FC<StopsFilterProps> = ({
  stopsFilter,
  onStopsFilterChange,
  displayedResultCount,
  hideInternalFilters = false,
  hideConnectedFilter = false,
}) => (
  <div
    className={`w-[210px] flex-shrink-0 ${hideInternalFilters ? "hidden" : "hidden md:block"}`}
  >
    <div className="sticky top-4 bg-white rounded-2xl shadow-lg border border-gray-200/60 overflow-hidden">
      {/* Dark Header */}
      <div className="flex items-center justify-between bg-[#1C242B] px-4 py-2.5">
        <h3 className="text-white text-[15px] font-medium tracking-tight">Filters</h3>
        <button
          type="button"
          className="text-white hover:opacity-80 transition-opacity p-0.5 rounded-full focus:outline-none"
          title="Reset filters"
          onClick={() => onStopsFilterChange("none")}
        >
          <RefreshCw className="w-4 h-4 text-white" />
        </button>
      </div>

      {/* Body */}
      <div className="px-4 pt-2.5 pb-3.5">
        <p className="text-[#717D96] text-xs font-normal mb-2">
          Showing {displayedResultCount} {displayedResultCount === 1 ? "result" : "results"}
        </p>

        <div className="mb-1">
          <p className="text-[#111827] text-sm font-semibold mb-2">Stops</p>
          <div className="flex flex-col gap-2">
            {OPTIONS.filter(
              (o) => !(o.connectedOnly && hideConnectedFilter),
            ).map((o) => {
              const isSelected = stopsFilter === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => onStopsFilterChange(o.value)}
                  className="flex items-center gap-2.5 py-0.5 text-left group cursor-pointer"
                >
                  <span
                    className={`flex-shrink-0 w-[18px] h-[18px] rounded-full border-2 transition-all ${
                      isSelected
                        ? "border-black bg-[#FFBC00]"
                        : "border-[#9CA3AF] bg-white group-hover:border-gray-500"
                    }`}
                  />
                  <span
                    className={`text-sm transition-colors ${
                      isSelected
                        ? "text-black font-semibold"
                        : "text-[#111827] font-medium group-hover:text-black"
                    }`}
                  >
                    {o.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default StopsFilter;
